/**
 * Spreaker Provider - Provider per integrazione Spreaker
 * 
 * Gestisce il recupero e la configurazione automatica di episodi Spreaker
 * per Sasha Player. Gli ads sono già inclusi nello stream URL di Spreaker.
 * 
 * @since v3.3.0 - Integrazione Spreaker
 */

import { SpreakerParser } from '../parsers/SpreakerParser.js';

export class SpreakerProvider {
  /**
   * Costruttore
   */
  constructor() {
    this.parser = new SpreakerParser();
    this.cache = new Map(); // Cache per evitare fetch multipli
  }
  
  /**
   * Recupera configurazione episodio Spreaker
   * 
   * @param {string|number} episodeId - ID episodio Spreaker
   * @param {Object} options - Opzioni (useCache, etc.)
   * @returns {Promise<Object>} Configurazione Sasha per l'episodio
   */
  async fetchEpisode(episodeId, options = {}) {
    const cacheKey = `episode_${episodeId}`;
    const useCache = options.useCache !== false;
    
    // Verifica cache
    if (useCache && this.cache.has(cacheKey)) {
      console.log('📦 Using cached Spreaker episode:', episodeId);
      return this.cache.get(cacheKey);
    }
    
    try {
      // Parse episodio
      const episodeData = await this.parser.parse(episodeId);
      
      // Cache risultato
      if (useCache) {
        this.cache.set(cacheKey, episodeData);
      }
      
      return episodeData;
    } catch (error) {
      console.error('❌ SpreakerProvider error:', error);
      
      // Fallback: restituisci config minimale con stream URL
      return {
        audioUrl: `https://api.spreaker.com/listen/episode/${episodeId}/http`,
        title: `Spreaker Episode ${episodeId}`,
        subtitle: '',
        coverImage: '',
        ads: [],
        _spreaker: true,
        _spreakerEpisodeId: episodeId,
        _error: error.message
      };
    }
  }
  
  /**
   * Pulisci cache
   * 
   * @param {string|number} episodeId - ID episodio da rimuovere dalla cache (opzionale)
   */
  clearCache(episodeId = null) {
    if (episodeId) {
      const cacheKey = `episode_${episodeId}`;
      this.cache.delete(cacheKey);
    } else {
      this.cache.clear();
    }
  }
  
  /**
   * Verifica se un valore è un episode ID Spreaker valido
   * 
   * @param {any} value - Valore da verificare
   * @returns {boolean} True se è un episode ID valido
   */
  static isValidEpisodeId(value) {
    if (!value) return false;
    
    // Accetta numeri o stringhe numeriche
    const num = typeof value === 'string' ? parseInt(value, 10) : value;
    return !isNaN(num) && num > 0;
  }
}
